import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import Layout from './Layout';
import { useAuth } from '../context/AuthContext';
import './AdminUsers.css';

const PAGE_SIZE = 8;

const ROLE_OPTIONS = [
  { value: 'agent', label: 'Agent' },
  { value: 'picker', label: 'Picker' },
  { value: 'admin', label: 'Admin' },
];

/** API / JSON may use number or string ids; strict === breaks find() and disables Save. */
function sameUserId(a, b) {
  if (a == null || b == null) return false;
  return String(a) === String(b);
}

export default function AdminUsers() {
  const { fetchWithAuth } = useAuth();
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [createRole, setCreateRole] = useState('agent');
  const [consignmentLocationId, setConsignmentLocationId] = useState('');

  const [searchEmail, setSearchEmail] = useState('');
  const [searchName, setSearchName] = useState('');
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState(null);
  const [detailTab, setDetailTab] = useState(0);
  const [detailRole, setDetailRole] = useState('agent');
  const [detailLocationId, setDetailLocationId] = useState('');
  const [saving, setSaving] = useState(false);
  /** Avoid resetting role/location from server on every `users` array refresh while the user is editing. */
  const lastServerSyncKeyRef = useRef('');
  const prevSelectedIdRef = useRef(null);

  const loadUsers = useCallback(async () => {
    try {
      setError('');
      const res = await fetchWithAuth('/api/users');
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to load users');
      setUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth]);

  const loadLocations = useCallback(async () => {
    try {
      const res = await fetchWithAuth('/api/consignment-locations');
      const data = await res.json();
      setLocations(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    }
  }, [fetchWithAuth]);

  useEffect(() => {
    loadUsers();
    loadLocations();
  }, [loadUsers, loadLocations]);

  const filteredUsers = useMemo(() => {
    const qe = searchEmail.trim().toLowerCase();
    const qn = searchName.trim().toLowerCase();
    return users.filter((u) => {
      const em = (u.email || '').toLowerCase();
      const dn = (u.displayName || '').toLowerCase();
      if (qe && !em.includes(qe)) return false;
      if (qn && !dn.includes(qn) && !em.includes(qn)) return false;
      return true;
    });
  }, [users, searchEmail, searchName]);

  const pageCount = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pageUsers = useMemo(() => {
    const p = Math.min(Math.max(1, safePage), pageCount);
    const start = (p - 1) * PAGE_SIZE;
    return filteredUsers.slice(start, start + PAGE_SIZE);
  }, [filteredUsers, safePage, pageCount]);

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const selected = useMemo(
    () => (selectedId == null ? null : users.find((u) => sameUserId(u.id, selectedId)) || null),
    [users, selectedId]
  );

  useEffect(() => {
    if (selectedId == null) {
      setDetailRole('agent');
      setDetailLocationId('');
      lastServerSyncKeyRef.current = '';
      prevSelectedIdRef.current = null;
      return;
    }
    if (prevSelectedIdRef.current !== selectedId) {
      prevSelectedIdRef.current = selectedId;
      lastServerSyncKeyRef.current = '';
    }
    const u = users.find((x) => sameUserId(x.id, selectedId));
    if (!u) return;
    const serverKey = `${u.id}|${u.role || 'agent'}|${u.consignmentLocationId ?? ''}`;
    if (serverKey === lastServerSyncKeyRef.current) return;
    lastServerSyncKeyRef.current = serverKey;
    setDetailRole(u.role || 'agent');
    setDetailLocationId(u.consignmentLocationId != null ? String(u.consignmentLocationId) : '');
  }, [selectedId, users]);

  const detailDirty = selected
    ? detailRole !== (selected.role || 'agent') ||
      String(detailLocationId || '') !==
        (selected.consignmentLocationId != null ? String(selected.consignmentLocationId) : '')
    : false;

  const handleOpenCreate = () => {
    setEmail('');
    setPassword('');
    setDisplayName('');
    setCreateRole('agent');
    setConsignmentLocationId('');
    setOpen(true);
  };

  const handleCreateUser = async () => {
    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail || !password) {
      setError('Email and password are required');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    try {
      setError('');
      const loc =
        createRole === 'picker' ? null : consignmentLocationId ? consignmentLocationId : null;
      const res = await fetchWithAuth('/api/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: trimmedEmail,
          password,
          displayName: displayName.trim() || undefined,
          role: createRole,
          consignmentLocationId: loc,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Create failed');
      setOpen(false);
      await loadUsers();
      if (data.id) setSelectedId(data.id);
    } catch (err) {
      setError(err.message || 'Failed to create user');
    }
  };

  const handleSaveDetails = async () => {
    if (selectedId == null) return;
    const row = users.find((u) => sameUserId(u.id, selectedId));
    if (!row) return;
    try {
      setSaving(true);
      setError('');
      const loc =
        detailRole === 'picker' ? null : detailLocationId ? detailLocationId : null;
      const res = await fetchWithAuth(`/api/users/${row.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          role: detailRole,
          consignmentLocationId: loc,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Update failed');
      const savedRole = data.role || 'agent';
      const savedLoc = data.consignmentLocationId ?? null;
      setUsers((prev) =>
        prev.map((u) =>
          sameUserId(u.id, data.id)
            ? {
                ...u,
                role: savedRole,
                consignmentLocationId: savedLoc,
                consignmentLocationName: data.consignmentLocationName ?? u.consignmentLocationName,
              }
            : u
        )
      );
      lastServerSyncKeyRef.current = `${data.id}|${savedRole}|${savedLoc ?? ''}`;
      setDetailRole(savedRole);
      setDetailLocationId(savedLoc != null ? String(savedLoc) : '');
      await loadUsers();
    } catch (err) {
      setError(err.message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const runSearch = () => {
    setPage(1);
  };

  const handleSelectRow = (u) => {
    setSelectedId(u.id);
    setDetailTab(0);
  };

  return (
    <Layout>
      <Box className="admin-users-page" sx={{ p: 2 }}>
        <Typography variant="h5" sx={{ mb: 0.5, fontWeight: 700 }}>
          Users
        </Typography>

        {error && (
          <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <div className="admin-users-toolbar">
          <Button startIcon={<AddIcon />} variant="contained" onClick={handleOpenCreate}>
            New
          </Button>
          <Button startIcon={<SaveIcon />} variant="outlined" disabled={!selected || !detailDirty || saving} onClick={handleSaveDetails}>
            Save
          </Button>
          <Divider orientation="vertical" flexItem />
          <Button startIcon={<RefreshIcon />} variant="outlined" onClick={() => { setLoading(true); loadUsers(); }} disabled={loading}>
            Refresh
          </Button>
        </div>

        {loading ? (
          <Typography color="text.secondary">Loading…</Typography>
        ) : (
          <div className="admin-users-split">
            <div className="admin-users-master">
              <div className="admin-users-section-header">Search</div>
              <div className="admin-users-search-body">
                <TextField
                  size="small"
                  fullWidth
                  label="Email"
                  value={searchEmail}
                  onChange={(e) => setSearchEmail(e.target.value)}
                  margin="dense"
                  sx={{ mt: 0 }}
                />
                <TextField
                  size="small"
                  fullWidth
                  label="Display name"
                  value={searchName}
                  onChange={(e) => setSearchName(e.target.value)}
                  margin="dense"
                />
                <div className="admin-users-search-actions">
                  <Button variant="contained" size="small" onClick={runSearch}>
                    Search
                  </Button>
                </div>
              </div>
              <div className="admin-users-list-scroll">
                <table className="admin-users-list-table">
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Name</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageUsers.length === 0 ? (
                      <tr>
                        <td colSpan={2} style={{ cursor: 'default', textAlign: 'center', color: '#6b7280' }}>
                          No users match.
                        </td>
                      </tr>
                    ) : (
                      pageUsers.map((u) => (
                        <tr
                          key={u.id}
                          className={sameUserId(selectedId, u.id) ? 'admin-users-row-selected' : ''}
                          onClick={() => handleSelectRow(u)}
                        >
                          <td>{u.email}</td>
                          <td>{u.displayName || '—'}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {filteredUsers.length > PAGE_SIZE && (
                <div className="admin-users-pagination">
                  <button type="button" disabled={safePage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                    ‹
                  </button>
                  {Array.from({ length: pageCount }, (_, i) => i + 1).map((num) => (
                    <button
                      key={num}
                      type="button"
                      className={num === safePage ? 'admin-users-page-current' : ''}
                      onClick={() => setPage(num)}
                    >
                      {num}
                    </button>
                  ))}
                  <button
                    type="button"
                    disabled={safePage >= pageCount}
                    onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                  >
                    ›
                  </button>
                </div>
              )}
            </div>

            <div className="admin-users-detail">
              <div className="admin-users-section-header">
                {selected ? `User details: ${selected.email}` : 'User details'}
              </div>
              {!selected ? (
                <div className="admin-users-detail-empty">Select a user from the list.</div>
              ) : (
                <>
                  <Tabs
                    value={detailTab}
                    onChange={(_, v) => setDetailTab(v)}
                    sx={{ borderBottom: 1, borderColor: 'divider', px: 1, minHeight: 42 }}
                  >
                    <Tab label="General" sx={{ textTransform: 'none', minHeight: 42 }} />
                    <Tab label="Location groups" disabled sx={{ textTransform: 'none', minHeight: 42 }} />
                    <Tab label="Notifications" disabled sx={{ textTransform: 'none', minHeight: 42 }} />
                    <Tab label="Memo" disabled sx={{ textTransform: 'none', minHeight: 42 }} />
                  </Tabs>
                  <div className="admin-users-detail-body">
                    {detailTab === 0 && (
                      <div className="admin-users-form-grid">
                        <TextField
                          label="Display name"
                          size="small"
                          value={selected.displayName || ''}
                          InputProps={{ readOnly: true }}
                          fullWidth
                        />
                        <TextField
                          label="Email"
                          size="small"
                          value={selected.email || ''}
                          InputProps={{ readOnly: true }}
                          fullWidth
                        />
                        <FormControl size="small" fullWidth>
                          <InputLabel>Role</InputLabel>
                          <Select
                            value={detailRole}
                            label="Role"
                            onChange={(e) => {
                              const v = e.target.value;
                              setDetailRole(v);
                              if (v === 'picker') setDetailLocationId('');
                            }}
                          >
                            {ROLE_OPTIONS.map((o) => (
                              <MenuItem key={o.value} value={o.value}>
                                {o.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <FormControl size="small" fullWidth disabled={detailRole === 'picker'}>
                          <InputLabel>Consignment location</InputLabel>
                          <Select
                            value={detailRole === 'picker' ? '' : detailLocationId}
                            label="Consignment location"
                            onChange={(e) => setDetailLocationId(e.target.value)}
                          >
                            <MenuItem value="">None</MenuItem>
                            {locations.map((loc) => (
                              <MenuItem key={loc.id} value={String(loc.id)}>
                                {loc.name} {loc.code ? `(${loc.code})` : ''}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Typography variant="caption" color="text.secondary" sx={{ gridColumn: '1 / -1' }}>
                          Created: {selected.createdAt ? new Date(selected.createdAt).toLocaleString() : '—'}
                        </Typography>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Create user</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Email"
              type="email"
              fullWidth
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <TextField
              margin="dense"
              label="Password"
              type="password"
              fullWidth
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              helperText="At least 6 characters"
            />
            <TextField
              margin="dense"
              label="Display name (optional)"
              fullWidth
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
            <FormControl fullWidth margin="dense">
              <InputLabel>Role</InputLabel>
              <Select
                value={createRole}
                label="Role"
                onChange={(e) => {
                  const v = e.target.value;
                  setCreateRole(v);
                  if (v === 'picker') setConsignmentLocationId('');
                }}
              >
                {ROLE_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {createRole !== 'picker' && (
              <FormControl fullWidth margin="dense">
                <InputLabel>Consignment location</InputLabel>
                <Select
                  value={consignmentLocationId}
                  label="Consignment location"
                  onChange={(e) => setConsignmentLocationId(e.target.value)}
                >
                  <MenuItem value="">None</MenuItem>
                  {locations.map((loc) => (
                    <MenuItem key={loc.id} value={loc.id}>
                      {loc.name} {loc.code ? `(${loc.code})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {createRole === 'picker' && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Pickers are internal users for pick lists; they are not assigned to a consignment location.
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateUser} variant="contained">
              Create
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
}
